import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Form, message } from 'antd';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useProjectMilestones } from '@hooks/projects/useProjectMilestones';
import { useProjectBugs, PROJECT_BUGS_QUERY_KEY } from '@hooks/projects/useProjectBugs';
import { useProjectQASubmissions, PROJECT_QA_SUBMISSIONS_QUERY_KEY } from './useProjectQASubmissions';
import { useProjectDetail } from './detail/useProjectDetail';
import {
    getProjectSprintTaskDetail,
    getProjectPhases,
    submitProjectToQA,
    getProjectSprintsForMilestone,
    getProjectSprintsForPhase,
    getProjectSprintsStandalone,
    resolveProjectSprintTask,
    revertProjectSprintTaskToQA,
} from '@services/projects.service';
import { getProjectTasks } from '@/services/task/task.service';
import {
    mergeBugsIntoSprintTasks,
    mergeQaSubmissionSprintsWithBugSprints,
    milestoneHasSprintsWithTasks,
    countSprintQaTasks,
    normalizeSubmissionAttachmentRecords,
    partitionQaSprintTasks,
    sortQaKanbanTasks,
    buildTaskDescription,
    getSprintTaskApiId,
    getTaskDescriptionMarkdown,
} from '@hooks/projects/projectHelpers';
import { formatLongDateTimeWithPipe } from '@utils/dateFormating.util';
import { contentToApiMentions } from '@utils/richTextMentions';
import { resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';
import { htmlToMarkdown } from '@utils/htmlToMarkdown.util';
import { invalidateProjectQueries } from './projectQueryInvalidation';
import { validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';

const MENTION_USERS_LIMIT = 100;
const QA_BUG_DRAFT_STORAGE_PREFIX = 'project-qa-bug-drafts';
const PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY = (projectId, sprintId, taskId) => [
    'projects',
    projectId,
    'sprints',
    sprintId,
    'tasks',
    taskId,
    'detail',
];

function extractTaskDetailPayload(raw, fallbackTask = null) {
    if (!raw || typeof raw !== 'object') {
        return {
            task: fallbackTask,
            bugs: Array.isArray(fallbackTask?.bugs) ? fallbackTask.bugs : [],
        };
    }
    const taskCandidate =
        (raw.task && typeof raw.task === 'object' ? raw.task : null) ??
        (raw.data && typeof raw.data === 'object' ? raw.data : null);
    const task = taskCandidate ?? raw;
    const bugs = Array.isArray(raw.bugs)
        ? raw.bugs
        : Array.isArray(task?.bugs)
            ? task.bugs
            : Array.isArray(fallbackTask?.bugs)
                ? fallbackTask.bugs
                : [];
    return { task, bugs };
}

function getQaBugDraftTaskId(task) {
    return task?.taskId ?? task?.id ?? task?.sprintTaskId ?? null;
}

function getQaBugDraftStorageKey(projectId, taskId) {
    const pid = String(projectId ?? '').trim();
    const tid = String(taskId ?? '').trim();
    if (!pid || !tid) return '';
    return `${QA_BUG_DRAFT_STORAGE_PREFIX}:${pid}:${tid}`;
}

function loadQaBugDrafts(projectId, taskId) {
    const key = getQaBugDraftStorageKey(projectId, taskId);
    if (!key || typeof window === 'undefined' || !window.localStorage) return [];
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function saveQaBugDrafts(projectId, taskId, drafts) {
    const key = getQaBugDraftStorageKey(projectId, taskId);
    if (!key || typeof window === 'undefined' || !window.localStorage) return;
    try {
        const list = Array.isArray(drafts) ? drafts : [];
        if (list.length === 0) {
            window.localStorage.removeItem(key);
            return;
        }
        window.localStorage.setItem(key, JSON.stringify(list));
    } catch {
        // ignore storage failures
    }
}

function extractSubmissionId(sub) {
    return sub?.id ?? sub?._id ?? sub?.submissionId ?? sub?.sid ?? sub?.submission_id ?? null;
}

function getSubmissionNumber(sub, fallbackIndex) {
    const n = sub?.submissionNumber ?? sub?.submission_number ?? sub?.number ?? null;
    if (n != null && String(n).trim() !== '' && String(n) !== 'undefined') return n;
    return fallbackIndex != null ? fallbackIndex + 1 : null;
}

function getSubmissionSubmittedAt(sub) {
    const raw = sub?.submittedAt ?? sub?.submitted_at ?? sub?.createdAt ?? sub?.created_at ?? null;
    return raw ? formatLongDateTimeWithPipe(raw) : null;
}

function getSubmissionSubmittedBy(sub) {
    // Expected shape: submittedBy: { name, id }
    const u = sub?.submittedBy ?? sub?.submitted_by ?? sub?.createdBy ?? sub?.created_by ?? null;
    if (!u || typeof u !== 'object') return null;
    const name = u?.name ?? sub?.submittedByName ?? sub?.submitted_by_name ?? null;
    const id = u?.id ?? u?._id ?? null;
    if (!name && !id) return null;
    return { name: name ?? 'User', id };
}


/** Valid task ids for PMO submit (drops junk like "undefined" from bad drag state). */
function normalizePmoTaskIds(ids) {
    const list = Array.isArray(ids) ? ids : [];
    return list
        .map((id) => (id == null ? '' : String(id).trim()))
        .filter((s) => s.length > 0 && s !== 'undefined');
}

// Native HTML5 drag/drop removed (dnd-kit is used by the QA board UI).

/**
 * QA tab: milestone/sprint board, sorted Kanban columns, drag-drop, PMO submit, task modal + pending bugs.
 */
export function useProjectQA(projectId, projectProp) {
    const queryClient = useQueryClient();
    const { data: projectFromQuery } = useProjectDetail(projectId, { enabled: !projectProp && !!projectId });
    const project = projectProp || projectFromQuery;
    const useMilestonesEnabled = project?.capabilities?.useMilestones !== false;
    const usePhasesEnabled = project?.capabilities?.usePhases !== false;
    const useSprintsEnabled = project?.capabilities?.useSprints !== false;
    const noSprintsMode = !useSprintsEnabled;

    const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);
    const [selectedSprintId, setSelectedSprintId] = useState(null);

    const { milestones, isLoading: milestonesLoading } = useProjectMilestones(projectId, { project });
    const { bugs, flatSprints: bugDerivedFlatSprints, isLoading: bugsLoading, createBugAsync, createBugPending } =
        useProjectBugs(projectId, { project });
    const {
        submissions: qaSubmissions,
        pmoSubmissions = [],
        flatSprints: qaFlatSprints,
        isLoading: submissionsLoading,
    } = useProjectQASubmissions(projectId, {
        project,
        sprintId: selectedSprintId,
        milestoneId: selectedMilestoneId,
    });

    /**
     * Union QA submission sprints with sprints implied by GET /projects/:id/bugs so tasks with bugs show
     * in "Bug reported" even when the task is not yet on a submission payload, then merge full bug list onto tasks.
     */

    const { data: phasesData = [], isLoading: phasesLoading } = useQuery({
        queryKey: ['projects', projectId, 'phases'],
        queryFn: () => getProjectPhases(projectId),
        enabled: Boolean(projectId && usePhasesEnabled && !useMilestonesEnabled),
        staleTime: 0,
    });
    const phases = useMemo(() => (Array.isArray(phasesData) ? phasesData : []), [phasesData]);

    const sprintsQueryKey = useMilestonesEnabled
        ? ['projects', projectId, 'milestones', selectedMilestoneId, 'sprints']
        : (usePhasesEnabled
            ? ['projects', projectId, 'phases', selectedMilestoneId, 'sprints']
            : ['projects', projectId, 'sprints', 'standalone']);

    const sprintsQueryFn = () => {
        if (useMilestonesEnabled) {
            return getProjectSprintsForMilestone(projectId, selectedMilestoneId);
        }
        if (usePhasesEnabled) {
            return getProjectSprintsForPhase(projectId, selectedMilestoneId);
        }
        return getProjectSprintsStandalone(projectId);
    };

    const isSprintsQueryEnabled = Boolean(
        projectId &&
        useSprintsEnabled &&
        (useMilestonesEnabled ? !!selectedMilestoneId : (usePhasesEnabled ? !!selectedMilestoneId : true))
    );

    const {
        data: fetchedSprintsData = [],
        isLoading: isSprintQueryLoading,
    } = useQuery({
        queryKey: sprintsQueryKey,
        queryFn: sprintsQueryFn,
        enabled: isSprintsQueryEnabled,
        staleTime: 0,
    });

    const tasksQueryParam = useMilestonesEnabled && selectedMilestoneId
        ? { milestoneId: selectedMilestoneId }
        : usePhasesEnabled && selectedMilestoneId
            ? { phaseId: selectedMilestoneId }
            : {};
    const tasksQueryKey = ['projects', projectId, 'tasks', selectedMilestoneId ?? null];
    // When milestones or phases are enabled, never fetch all tasks — wait for a
    // milestone/phase to be selected first so we always use the scoped endpoint.
    const tasksQueryEnabled = Boolean(projectId) && (
        useMilestonesEnabled ? !!selectedMilestoneId
        : usePhasesEnabled ? !!selectedMilestoneId
        : true
    );

    const {
        data: projectTasks = [],
        isLoading: isProjectTasksLoading,
    } = useQuery({
        queryKey: tasksQueryKey,
        queryFn: () => getProjectTasks(projectId, tasksQueryParam),
        enabled: tasksQueryEnabled,
        staleTime: 0,
    });

    const sprints = useMemo(() => {
        const combined = mergeQaSubmissionSprintsWithBugSprints(qaFlatSprints, bugDerivedFlatSprints);
        const merged = mergeBugsIntoSprintTasks(combined, bugs);

        const apiSprints = Array.isArray(fetchedSprintsData) ? fetchedSprintsData : [];
        const mergedMap = new Map(merged.map((s) => [String(s.id), s]));
        const finalSprints = [...merged];

        for (const apiSprint of apiSprints) {
            const apiSprintId = String(apiSprint.id);
            if (!mergedMap.has(apiSprintId)) {
                finalSprints.push({
                    ...apiSprint,
                    phaseId: usePhasesEnabled ? selectedMilestoneId : undefined,
                    milestoneId: useMilestonesEnabled ? selectedMilestoneId : undefined,
                    tasks: apiSprint.tasks || [],
                });
            } else {
                const existing = mergedMap.get(apiSprintId);
                if (usePhasesEnabled && !existing.phaseId && !existing.phase?.id) {
                    existing.phaseId = selectedMilestoneId;
                }
                if (useMilestonesEnabled && !existing.milestoneId && !existing.milestone?.id) {
                    existing.milestoneId = selectedMilestoneId;
                }
            }
        }

        // Sort finalSprints to preserve API order chronologically
        const apiSprintIndexMap = new Map(apiSprints.map((s, idx) => [String(s.id), idx]));
        finalSprints.sort((a, b) => {
            const indexA = apiSprintIndexMap.has(String(a.id)) ? apiSprintIndexMap.get(String(a.id)) : 999999;
            const indexB = apiSprintIndexMap.has(String(b.id)) ? apiSprintIndexMap.get(String(b.id)) : 999999;
            if (indexA !== indexB) {
                return indexA - indexB;
            }
            return String(a.name ?? a.id).localeCompare(String(b.name ?? b.id));
        });

        if (!useSprintsEnabled) {
            // In noSprintsMode, tasks may still have a real sprintId in the DB
            // (e.g. internal sprint "__code360_project_tasks__"). Collect all
            // tasks from every sprint in finalSprints and surface them under the
            // virtual 'no-sprint' bucket so they appear on the QA board.
            const noSprintEntry = finalSprints.find((s) => s.id === 'no-sprint');
            const allTasksFromSprints = finalSprints
                .filter((s) => s.id !== 'no-sprint')
                .flatMap((s) => s.tasks || []);

            // Filter projectTasks by selected phase or milestone
            const filteredProjectTasks = (projectTasks || []).filter((t) => {
                if (useMilestonesEnabled) {
                    return String(t.milestoneId ?? t.milestone_id ?? '') === String(selectedMilestoneId);
                }
                if (usePhasesEnabled) {
                    return String(t.phaseId ?? t.phase_id ?? '') === String(selectedMilestoneId);
                }
                return true;
            });

            const noSprintTasks = [
                ...(noSprintEntry?.tasks || []),
                ...allTasksFromSprints,
                ...filteredProjectTasks,
            ];
            // Deduplicate by task id
            const seen = new Set();
            const dedupedTasks = noSprintTasks.filter((t) => {
                const tid = String(t?.taskId ?? t?.id ?? '');
                if (!tid || seen.has(tid)) return false;
                seen.add(tid);
                return true;
            });
            return [{ ...(noSprintEntry ?? {}), id: 'no-sprint', name: 'No Sprint', tasks: dedupedTasks }];
        }
        const enrichedSprints = finalSprints
            .filter((s) => s.id !== 'no-sprint')
            .map((s) => {
                const sprintId = String(s.id);
                const matchingProjectTasks = (projectTasks || []).filter(
                    (t) => String(t.sprintId ?? t.sprint_id ?? '') === sprintId
                );
                const allTasks = [...(s.tasks || []), ...matchingProjectTasks];
                const seen = new Set();
                const dedupedTasks = [];
                for (const t of allTasks) {
                    const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                    if (tid != null) {
                        const strId = String(tid);
                        if (!seen.has(strId)) {
                            seen.add(strId);
                            dedupedTasks.push(t);
                        }
                    }
                }
                return {
                    ...s,
                    tasks: dedupedTasks,
                };
            });
        return enrichedSprints;
    }, [
        qaFlatSprints,
        bugDerivedFlatSprints,
        bugs,
        useSprintsEnabled,
        fetchedSprintsData,
        selectedMilestoneId,
        usePhasesEnabled,
        useMilestonesEnabled,
        projectTasks,
    ]);

    const milestoneList = useMemo(() => (Array.isArray(milestones) ? milestones : []), [milestones]);
    const sprintList = useMemo(() => (Array.isArray(sprints) ? sprints : []), [sprints]);

    const activeTabList = useMemo(() => {
        if (useMilestonesEnabled) return milestoneList;
        if (usePhasesEnabled) return phases;
        return [];
    }, [useMilestonesEnabled, milestoneList, usePhasesEnabled, phases]);

    const mentionUsers = useMemo(
        () => (project?.assignedUsers ?? []).slice(0, MENTION_USERS_LIMIT),
        [project?.assignedUsers],
    );
    const mentionMilestones = useMemo(() => activeTabList, [activeTabList]);
    const mentionSprints = useMemo(() => sprintList, [sprintList]);

    const sprintsForMilestone = useMemo(() => {
        if (!useSprintsEnabled) return sprintList;
        if (useMilestonesEnabled) {
            if (!milestoneList.length) return sprintList;
            if (!selectedMilestoneId) return [];
            return sprintList.filter(
                (s) => String(s.milestoneId ?? s.milestone?.id ?? '') === String(selectedMilestoneId),
            );
        }
        if (usePhasesEnabled) {
            if (!phases.length) return sprintList;
            if (!selectedMilestoneId) return [];
            return sprintList.filter(
                (s) => String(s.phaseId ?? s.phase?.id ?? s.milestoneId ?? s.milestone?.id ?? '') === String(selectedMilestoneId),
            );
        }
        return sprintList;
    }, [milestoneList.length, phases.length, sprintList, selectedMilestoneId, useMilestonesEnabled, usePhasesEnabled, useSprintsEnabled]);

    const selectedSprint = useMemo(() => {
        if (!useSprintsEnabled) return sprintList[0] ?? null;
        return sprintsForMilestone.find((s) => String(s.id) === String(selectedSprintId)) ?? null;
    }, [sprintsForMilestone, selectedSprintId, useSprintsEnabled, sprintList]);

    const { todos: todosRaw, bugsReported: bugsRaw, resolved: resolvedRaw, submittedToPmo: pmoRaw } = useMemo(
        () => partitionQaSprintTasks(selectedSprint?.tasks),
        [selectedSprint?.tasks],
    );

    const [pmoStagedTaskIds, setPmoStagedTaskIds] = useState([]);
    const [pmoStagedTasks, setPmoStagedTasks] = useState([]);
    const [pmoStagedCommitted, setPmoStagedCommitted] = useState(false);

    const pmoStagedIdSet = useMemo(
        () => new Set((Array.isArray(pmoStagedTaskIds) ? pmoStagedTaskIds : []).map((x) => String(x))),
        [pmoStagedTaskIds],
    );

    const todos = useMemo(() => {
        const list = sortQaKanbanTasks(todosRaw);
        if (pmoStagedIdSet.size === 0) return list;
        return list.filter((t) => {
            const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
            return tid == null ? true : !pmoStagedIdSet.has(String(tid));
        });
    }, [todosRaw, pmoStagedIdSet]);

    const bugsReported = useMemo(() => sortQaKanbanTasks(bugsRaw), [bugsRaw]);

    const resolvedTasks = useMemo(() => {
        const list = sortQaKanbanTasks(resolvedRaw);
        if (pmoStagedIdSet.size === 0) return list;
        return list.filter((t) => {
            const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
            return tid == null ? true : !pmoStagedIdSet.has(String(tid));
        });
    }, [resolvedRaw, pmoStagedIdSet]);

    const submittedToPmo = useMemo(() => {
        const base = sortQaKanbanTasks(pmoRaw);
        const baseSet = new Set(
            base
                .map((t) => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId)
                .filter((x) => x != null)
                .map(String),
        );
        const staged = (Array.isArray(pmoStagedTasks) ? pmoStagedTasks : []).filter((t) => {
            const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
            return tid == null ? false : !baseSet.has(String(tid));
        });
        // Staged tasks appear at top while waiting for server confirmation.
        return staged.length ? [...staged, ...base] : base;
    }, [pmoRaw, pmoStagedTasks]);

    useEffect(() => {
        if (!useMilestonesEnabled && !usePhasesEnabled) {
            setSelectedMilestoneId(null);
            return;
        }
        if (!activeTabList.length) {
            setSelectedMilestoneId(null);
            return;
        }
        setSelectedMilestoneId((prev) => {
            if (prev != null && activeTabList.some((m) => String(m.id) === String(prev))) {
                if (useMilestonesEnabled) {
                    if (milestoneHasSprintsWithTasks(prev, sprintList)) return prev;
                } else if (usePhasesEnabled) {
                    const hasTasks = sprintList.some(
                        (s) =>
                            String(s.phaseId ?? s.phase?.id ?? s.milestoneId ?? s.milestone?.id ?? '') === String(prev) &&
                            countSprintQaTasks(s) > 0,
                    );
                    if (hasTasks) return prev;
                }
                return prev;
            }
            const withTasks = activeTabList.find((m) => {
                if (useMilestonesEnabled) {
                    return milestoneHasSprintsWithTasks(m.id, sprintList);
                } else if (usePhasesEnabled) {
                    return sprintList.some(
                        (s) =>
                            String(s.phaseId ?? s.phase?.id ?? s.milestoneId ?? s.milestone?.id ?? '') === String(m.id) &&
                            countSprintQaTasks(s) > 0,
                    );
                }
                return false;
            });
            return withTasks?.id ?? activeTabList[0]?.id ?? null;
        });
    }, [activeTabList, sprintList, useMilestonesEnabled, usePhasesEnabled]);

    useEffect(() => {
        if (!useSprintsEnabled) {
            setSelectedSprintId('no-sprint');
            return;
        }
        if (!sprintsForMilestone.length) {
            setSelectedSprintId(null);
            return;
        }
        const withTasks = sprintsForMilestone.filter((s) => countSprintQaTasks(s) > 0);
        setSelectedSprintId((prev) => {
            if (prev != null && sprintsForMilestone.some((s) => String(s.id) === String(prev))) return prev;
            if (withTasks.length > 0) return withTasks[0]?.id;
            return sprintsForMilestone[0]?.id ?? null;
        });
    }, [sprintsForMilestone, useSprintsEnabled]);

    const pmoMutation = useMutation({
        mutationFn: (payload) => {
            const taskIds = normalizePmoTaskIds(payload.taskIds);
            // When both are sent, some backends treat sprintIds as "submit whole sprint" and ignore taskIds.
            // Prefer task-scoped PMO submit when we have explicit task ids.
            const body = {
                milestoneId: payload.milestoneId,
                submittedTo: 'pmo',
                notes: payload.notesMarkdown ? { markdown: payload.notesMarkdown } : undefined,
                attachmentUrls: payload.attachmentUrls,
                files: payload.files,
            };
            if (taskIds.length > 0) {
                body.taskIds = taskIds;
            } else if (payload.sprintIds != null) {
                body.sprintIds = payload.sprintIds;
            }
            return submitProjectToQA(projectId, body);
        },
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
            queryClient.invalidateQueries(PROJECT_QA_SUBMISSIONS_QUERY_KEY(projectId));
        },
    });

    const submitToPMO = useCallback(
        ({ milestoneId, sprintIds, taskIds, notesMarkdown, attachmentUrls, files, onSuccess, onError }) => {
            pmoMutation.mutate(
                {
                    milestoneId,
                    sprintIds,
                    taskIds,
                    notesMarkdown,
                    attachmentUrls,
                    files,
                },
                {
                    onSuccess: () => {
                        onSuccess?.();
                    },
                    onError,
                },
            );
        },
        [pmoMutation],
    );

    const submitToPMOPending = pmoMutation.isPending;

    const [pmoSubmissionOpen, setPmoSubmissionOpen] = useState(false);
    const [pmoSubmissionItems, setPmoSubmissionItems] = useState([]);
    const [pmoSubmissionDescriptionHtml, setPmoSubmissionDescriptionHtml] = useState('');
    const [pmoSubmissionPendingFiles, setPmoSubmissionPendingFiles] = useState([]);
    const [pmoSubmissionPendingLinks, setPmoSubmissionPendingLinks] = useState([]);
    const [pmoLinkModalOpen, setPmoLinkModalOpen] = useState(false);
    const [pmoLinkForm] = Form.useForm();
    const pmoLinkCallbackRef = useRef(null);
    const [pmoSubmissionSession, setPmoSubmissionSession] = useState(0);
    const wasPmoPanelOpenRef = useRef(false);

    useEffect(() => {
        if (pmoSubmissionOpen && !wasPmoPanelOpenRef.current) {
            setPmoSubmissionSession((s) => s + 1);
        }
        wasPmoPanelOpenRef.current = pmoSubmissionOpen;
    }, [pmoSubmissionOpen]);

    const closePmoSubmissionPanel = useCallback((opts = {}) => {
        const { revertStaged = !pmoStagedCommitted } = opts || {};
        setPmoSubmissionOpen(false);
        setPmoSubmissionItems([]);
        setPmoSubmissionDescriptionHtml('');
        setPmoSubmissionPendingFiles((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return [];
        });
        setPmoSubmissionPendingLinks([]);
        setPmoLinkModalOpen(false);
        pmoLinkCallbackRef.current = null;
        try {
            pmoLinkForm.resetFields();
        } catch {
            // ignore
        }

        if (revertStaged) {
            setPmoStagedTaskIds([]);
            setPmoStagedTasks([]);
            setPmoStagedCommitted(false);
        }
    }, [pmoLinkForm, pmoStagedCommitted]);

    const removeFromPmoSubmission = useCallback((type, id) => {
        setPmoSubmissionItems((prev) =>
            prev.filter((i) => !(i.type === type && String(i.id) === String(id))),
        );
    }, []);

    const handlePmoFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImage = (file.type || '').startsWith('image/');
            return {
                id: `pmo-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImage ? URL.createObjectURL(file) : null,
            };
        });
        setPmoSubmissionPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const removePmoPendingFile = useCallback((id) => {
        setPmoSubmissionPendingFiles((prev) => {
            const index = prev.findIndex((p) => p.id === id);
            const removedAt = index >= 0 ? prev[index] : null;
            if (removedAt?.preview) {
                try {
                    URL.revokeObjectURL(removedAt.preview);
                } catch {
                    // ignore
                }
            }
            if (index < 0) return prev.filter((p) => p.id !== id);
            setPmoSubmissionDescriptionHtml((content) => {
                let c = content || '';
                const linkRegex = new RegExp(
                    `<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`,
                    'gi',
                );
                c = c.replace(linkRegex, '');
                for (let j = prev.length - 1; j > index; j--) {
                    c = c.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                return c;
            });
            return prev.filter((p) => p.id !== id);
        });
    }, []);

    const handlePmoLinkRequest = useCallback(
        (insertLink) => {
            pmoLinkCallbackRef.current = insertLink;
            pmoLinkForm.resetFields();
            setPmoLinkModalOpen(true);
        },
        [pmoLinkForm],
    );

    const closePmoLinkModal = useCallback(() => {
        setPmoLinkModalOpen(false);
        pmoLinkCallbackRef.current = null;
    }, []);

    const handlePmoLinkModalSubmit = useCallback(() => {
        pmoLinkForm.validateFields().then((values) => {
            const rawUrl = (values.url || '').trim();
            const title = (values.title || '').trim();
            const check = validateLinkUrlInput(rawUrl);
            if (!check.ok) {
                pmoLinkForm.setFields([
                    { name: 'url', errors: [check.message || 'Enter a valid URL (https://example.com)'] },
                ]);
                return;
            }
            const normalizedUrl = check.normalized;
            const displayTitle = title || normalizedUrl;
            const insertLink = pmoLinkCallbackRef.current;
            if (insertLink) insertLink({ url: normalizedUrl, title: displayTitle });
            setPmoSubmissionPendingLinks((prev) => [
                ...prev,
                { id: `link-${Date.now()}`, url: normalizedUrl, title: displayTitle },
            ]);
            setPmoLinkModalOpen(false);
            pmoLinkCallbackRef.current = null;
        });
    }, [pmoLinkForm]);

    const removePmoPendingLink = useCallback((idOrHref) => {
        const key = String(idOrHref ?? '').trim();
        if (!key) return;
        setPmoSubmissionPendingLinks((prev) => {
            const match =
                prev.find((x) => String(x.id) === key) ??
                prev.find((x) => String(x.url) === key);
            const next = prev.filter((x) => String(x.id) !== key && String(x.url) !== key);
            const href = match?.url || (key.startsWith('http') ? key : null);
            if (href) {
                const esc = href.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                setPmoSubmissionDescriptionHtml((content) => {
                    const c = String(content || '');
                    const re = new RegExp(`<a\\s(?=[^>]*href=["']${esc}["'])[^>]*>[\\s\\S]*?<\\/a>`, 'i');
                    return c.replace(re, '');
                });
            }
            return next;
        });
    }, []);

    const handlePmoEditorLinkRemoved = useCallback(
        (href) => {
            if (!href) return;
            removePmoPendingLink(String(href));
        },
        [removePmoPendingLink],
    );

    useEffect(() => {
        const html = pmoSubmissionDescriptionHtml;
        if (!html || typeof html !== 'string') {
            setPmoSubmissionPendingLinks([]);
            return;
        }
        setPmoSubmissionPendingLinks((prev) => prev.filter((x) => !x?.url || html.includes(String(x.url))));
    }, [pmoSubmissionDescriptionHtml]);

    const [qaTodoPmoSelectMode, setQaTodoPmoSelectMode] = useState(false);
    const [qaTodoPmoSelectedIds, setQaTodoPmoSelectedIds] = useState([]);
    const cancelQaTodoPmoSelectMode = useCallback(() => {
        setQaTodoPmoSelectMode(false);
        setQaTodoPmoSelectedIds([]);
    }, []);

    const [qaResolvedPmoSelectMode, setQaResolvedPmoSelectMode] = useState(false);
    const [qaResolvedPmoSelectedIds, setQaResolvedPmoSelectedIds] = useState([]);
    const cancelQaResolvedPmoSelectMode = useCallback(() => {
        setQaResolvedPmoSelectMode(false);
        setQaResolvedPmoSelectedIds([]);
    }, []);

    const addToPmoSubmissionFromDrop = useCallback(
        ({ sprintId, taskId }) => {
            const sprint = sprintList.find((s) => String(s.id) === String(sprintId));
            const task = sprint?.tasks?.find(
                (t) => String(getSprintTaskApiId(t) ?? t?.id ?? '') === String(taskId),
            );
            if (!sprint || !task) {
                message.error('Task not found');
                return;
            }
            const tid = getSprintTaskApiId(task) ?? taskId;
            if (tid == null || String(tid).trim() === '' || String(tid) === 'undefined') {
                message.error('Task is missing a valid id');
                return;
            }
            setPmoSubmissionItems((prev) => {
                const key = `task:${String(tid)}`;
                if (prev.some((i) => `task:${String(i.id)}` === key)) return prev;
                return [
                    ...prev,
                    {
                        type: 'task',
                        id: tid,
                        sprintId,
                        sprint,
                        task,
                    },
                ];
            });
            setPmoSubmissionOpen(true);
            cancelQaTodoPmoSelectMode();
        },
        [sprintList, cancelQaTodoPmoSelectMode],
    );

    const hasMixedTasksForSubmission = useCallback((taskList) => {
        if (!Array.isArray(taskList) || taskList.length <= 1) return false;
        const firstTask = taskList[0];
        const firstCameFromQa = Boolean(firstTask.cameFromQa || firstTask.came_from_qa);
        let firstSubId = null;
        if (firstCameFromQa) {
            const tId = getSprintTaskApiId(firstTask) ?? firstTask?.id ?? firstTask?.taskId;
            const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find(sub => 
                Array.isArray(sub?.tasks) && sub.tasks.some(tIdVal => String(tIdVal) === String(tId))
            );
            firstSubId = match ? (match.submissionId || match.id) : null;
        }
        
        for (let i = 1; i < taskList.length; i++) {
            const t = taskList[i];
            const currentCameFromQa = Boolean(t.cameFromQa || t.came_from_qa);
            if (currentCameFromQa !== firstCameFromQa) return true;
            if (currentCameFromQa) {
                const tId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find(sub => 
                    Array.isArray(sub?.tasks) && sub.tasks.some(tIdVal => String(tIdVal) === String(tId))
                );
                const currentSubId = match ? (match.submissionId || match.id) : null;
                if (currentSubId !== firstSubId) return true;
            }
        }
        return false;
    }, [qaSubmissions]);

    const isTodoPmoTaskDisabled = useCallback((t) => {
        if (!qaTodoPmoSelectMode || qaTodoPmoSelectedIds.length === 0) return false;
        
        // Find the first selected task in todos
        const firstSelectedId = String(qaTodoPmoSelectedIds[0]);
        const firstSelectedTask = (Array.isArray(todos) ? todos : []).find(
            (x) => String(getSprintTaskApiId(x) ?? x?.id ?? x?.taskId) === firstSelectedId
        );
        if (!firstSelectedTask) return false;
        
        const firstCameFromQa = Boolean(firstSelectedTask.cameFromQa || firstSelectedTask.came_from_qa);
        let firstSubId = null;
        if (firstCameFromQa) {
            const tId = getSprintTaskApiId(firstSelectedTask) ?? firstSelectedTask?.id ?? firstSelectedTask?.taskId;
            const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
            );
            firstSubId = match ? (match.submissionId || match.id) : null;
        }

        const currentCameFromQa = Boolean(t.cameFromQa || t.came_from_qa);
        let currentSubId = null;
        if (currentCameFromQa) {
            const tId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
            const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
            );
            currentSubId = match ? (match.submissionId || match.id) : null;
        }
        
        if (currentCameFromQa !== firstCameFromQa) {
            return true;
        }
        if (currentCameFromQa && currentSubId !== firstSubId) {
            return true;
        }
        return false;
    }, [qaTodoPmoSelectMode, qaTodoPmoSelectedIds, todos, qaSubmissions]);

    const isResolvedPmoTaskDisabled = useCallback((t) => {
        if (!qaResolvedPmoSelectMode || qaResolvedPmoSelectedIds.length === 0) return false;
        
        // Find the first selected task in resolvedTasks
        const firstSelectedId = String(qaResolvedPmoSelectedIds[0]);
        const firstSelectedTask = (Array.isArray(resolvedTasks) ? resolvedTasks : []).find(
            (x) => String(getSprintTaskApiId(x) ?? x?.id ?? x?.taskId) === firstSelectedId
        );
        if (!firstSelectedTask) return false;
        
        const firstCameFromQa = Boolean(firstSelectedTask.cameFromQa || firstSelectedTask.came_from_qa);
        let firstSubId = null;
        if (firstCameFromQa) {
            const tId = getSprintTaskApiId(firstSelectedTask) ?? firstSelectedTask?.id ?? firstSelectedTask?.taskId;
            const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
            );
            firstSubId = match ? (match.submissionId || match.id) : null;
        }

        const currentCameFromQa = Boolean(t.cameFromQa || t.came_from_qa);
        let currentSubId = null;
        if (currentCameFromQa) {
            const tId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
            const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
            );
            currentSubId = match ? (match.submissionId || match.id) : null;
        }
        
        if (currentCameFromQa !== firstCameFromQa) {
            return true;
        }
        if (currentCameFromQa && currentSubId !== firstSubId) {
            return true;
        }
        return false;
    }, [qaResolvedPmoSelectMode, qaResolvedPmoSelectedIds, resolvedTasks, qaSubmissions]);

    const startQaTodoPmoSelectMode = useCallback(() => {
        setQaTodoPmoSelectMode(true);
        setQaTodoPmoSelectedIds([]);
    }, []);

    const toggleQaTodoPmoTaskSelected = useCallback((taskId) => {
        const id = String(taskId ?? '');
        if (!id || id === 'undefined') return;

        // Find the task in todos
        const clickedTask = (Array.isArray(todos) ? todos : []).find(
            (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === id
        );
        if (!clickedTask) return;

        const cameFromQa = Boolean(clickedTask.cameFromQa || clickedTask.came_from_qa);
        let submissionId = null;
        if (cameFromQa) {
            const tId = getSprintTaskApiId(clickedTask) ?? clickedTask?.id ?? clickedTask?.taskId;
            const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
            );
            submissionId = match ? (match.submissionId || match.id) : null;
        }

        setQaTodoPmoSelectedIds((prev) => {
            const set = new Set(prev.map(String));
            
            if (cameFromQa && submissionId) {
                // Find all tasks in todos with the same submissionId
                const matchingTaskIds = (Array.isArray(todos) ? todos : [])
                    .filter((t) => {
                        const tId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                        const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                            Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
                        );
                        const tSubId = match ? (match.submissionId || match.id) : null;
                        return tSubId === submissionId;
                    })
                    .map((t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId))
                    .filter(Boolean);

                const anySelected = matchingTaskIds.some((mId) => set.has(mId));
                if (anySelected) {
                    matchingTaskIds.forEach((mId) => set.delete(mId));
                } else {
                    // Check if we are mixing with non-QA tasks or other QA submissions
                    if (prev.length > 0) {
                        const firstSelectedId = prev[0];
                        const firstTask = (Array.isArray(todos) ? todos : []).find(
                            (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === firstSelectedId
                        );
                        if (firstTask) {
                            const firstCameFromQa = Boolean(firstTask.cameFromQa || firstTask.came_from_qa);
                            let firstSubId = null;
                            if (firstCameFromQa) {
                                const tId = getSprintTaskApiId(firstTask) ?? firstTask?.id ?? firstTask?.taskId;
                                const firstMatch = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                                    Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
                                );
                                firstSubId = firstMatch ? (firstMatch.submissionId || firstMatch.id) : null;
                            }
                            if (!firstCameFromQa || firstSubId !== submissionId) {
                                message.warning('Cannot mix tasks from different QA submissions or standard tasks.');
                                return prev;
                            }
                        }
                    }
                    matchingTaskIds.forEach((mId) => set.add(mId));
                }
            } else {
                // Standard task (not cameFromQa)
                if (set.has(id)) {
                    set.delete(id);
                } else {
                    // Check if we are mixing with QA tasks
                    if (prev.length > 0) {
                        const firstSelectedId = prev[0];
                        const firstTask = (Array.isArray(todos) ? todos : []).find(
                            (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === firstSelectedId
                        );
                        if (firstTask && Boolean(firstTask.cameFromQa || firstTask.came_from_qa)) {
                            message.warning('Cannot mix standard tasks with tasks originating from QA.');
                            return prev;
                        }
                    }
                    set.add(id);
                }
            }
            return Array.from(set);
        });
    }, [todos, qaSubmissions]);

    const selectAllQaTodoTasks = useCallback(() => {
        if (hasMixedTasksForSubmission(todos)) {
            message.warning('Cannot select all because column contains tasks from different QA submissions or mixed standard tasks.');
            return;
        }
        const ids = (Array.isArray(todos) ? todos : [])
            .map((t) => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId)
            .filter((id) => id != null && String(id).trim() !== '' && String(id) !== 'undefined')
            .map(String);
        setQaTodoPmoSelectedIds(Array.from(new Set(ids)));
        setQaTodoPmoSelectMode(true);
    }, [todos, hasMixedTasksForSubmission]);

    const unselectAllQaTodoTasks = useCallback(() => {
        setQaTodoPmoSelectedIds([]);
        setQaTodoPmoSelectMode(true);
    }, []);

    const startQaResolvedPmoSelectMode = useCallback(() => {
        setQaResolvedPmoSelectMode(true);
        setQaResolvedPmoSelectedIds([]);
    }, []);

    const toggleQaResolvedPmoTaskSelected = useCallback((taskId) => {
        const id = String(taskId ?? '');
        if (!id || id === 'undefined') return;

        // Find the task in resolvedTasks
        const clickedTask = (Array.isArray(resolvedTasks) ? resolvedTasks : []).find(
            (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === id
        );
        if (!clickedTask) return;

        const cameFromQa = Boolean(clickedTask.cameFromQa || clickedTask.came_from_qa);
        let submissionId = null;
        if (cameFromQa) {
            const tId = getSprintTaskApiId(clickedTask) ?? clickedTask?.id ?? clickedTask?.taskId;
            const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
            );
            submissionId = match ? (match.submissionId || match.id) : null;
        }

        setQaResolvedPmoSelectedIds((prev) => {
            const set = new Set(prev.map(String));
            
            if (cameFromQa && submissionId) {
                // Find all tasks in resolvedTasks with the same submissionId
                const matchingTaskIds = (Array.isArray(resolvedTasks) ? resolvedTasks : [])
                    .filter((t) => {
                        const tId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                        const match = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                            Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
                        );
                        const tSubId = match ? (match.submissionId || match.id) : null;
                        return tSubId === submissionId;
                    })
                    .map((t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId))
                    .filter(Boolean);

                const anySelected = matchingTaskIds.some((mId) => set.has(mId));
                if (anySelected) {
                    matchingTaskIds.forEach((mId) => set.delete(mId));
                } else {
                    // Check if we are mixing with non-QA tasks or other QA submissions
                    if (prev.length > 0) {
                        const firstSelectedId = prev[0];
                        const firstTask = (Array.isArray(resolvedTasks) ? resolvedTasks : []).find(
                            (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === firstSelectedId
                        );
                        if (firstTask) {
                            const firstCameFromQa = Boolean(firstTask.cameFromQa || firstTask.came_from_qa);
                            let firstSubId = null;
                            if (firstCameFromQa) {
                                const tId = getSprintTaskApiId(firstTask) ?? firstTask?.id ?? firstTask?.taskId;
                                const firstMatch = (Array.isArray(qaSubmissions) ? qaSubmissions : []).find((sub) =>
                                    Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
                                );
                                firstSubId = firstMatch ? (firstMatch.submissionId || firstMatch.id) : null;
                            }
                            if (!firstCameFromQa || firstSubId !== submissionId) {
                                message.warning('Cannot mix tasks from different QA submissions or standard tasks.');
                                return prev;
                            }
                        }
                    }
                    matchingTaskIds.forEach((mId) => set.add(mId));
                }
            } else {
                // Standard task (not cameFromQa)
                if (set.has(id)) {
                    set.delete(id);
                } else {
                    // Check if we are mixing with QA tasks
                    if (prev.length > 0) {
                        const firstSelectedId = prev[0];
                        const firstTask = (Array.isArray(resolvedTasks) ? resolvedTasks : []).find(
                            (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === firstSelectedId
                        );
                        if (firstTask && Boolean(firstTask.cameFromQa || firstTask.came_from_qa)) {
                            message.warning('Cannot mix standard tasks with tasks originating from QA.');
                            return prev;
                        }
                    }
                    set.add(id);
                }
            }
            return Array.from(set);
        });
    }, [resolvedTasks, qaSubmissions]);

    const selectAllQaResolvedTasks = useCallback(() => {
        if (hasMixedTasksForSubmission(resolvedTasks)) {
            message.warning('Cannot select all because column contains tasks from different QA submissions or mixed standard tasks.');
            return;
        }
        const ids = (Array.isArray(resolvedTasks) ? resolvedTasks : [])
            .map((t) => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId)
            .filter((id) => id != null && String(id).trim() !== '' && String(id) !== 'undefined')
            .map(String);
        setQaResolvedPmoSelectedIds(Array.from(new Set(ids)));
        setQaResolvedPmoSelectMode(true);
    }, [resolvedTasks, hasMixedTasksForSubmission]);

    const unselectAllQaResolvedTasks = useCallback(() => {
        setQaResolvedPmoSelectedIds([]);
        setQaResolvedPmoSelectMode(true);
    }, []);

    const openPmoSubmissionWithTodoTasks = useCallback(
        (tasks) => {
            if (!selectedSprint) {
                message.error('No sprint selected');
                return;
            }
            const list = Array.isArray(tasks) ? tasks : [];
            if (list.length === 0) {
                message.warning('No tasks to submit');
                return;
            }
            const sprintId = selectedSprint.id;
            const seen = new Set();
            const items = [];
            for (const task of list) {
                const tid = getSprintTaskApiId(task) ?? task?.id;
                if (tid == null || String(tid).trim() === '' || String(tid) === 'undefined') continue;
                const key = String(tid);
                if (seen.has(key)) continue;
                seen.add(key);
                items.push({
                    type: 'task',
                    id: tid,
                    sprintId,
                    sprint: selectedSprint,
                    task,
                });
            }
            if (items.length === 0) {
                message.error('No valid tasks to submit');
                return;
            }
            // Stage cards into Submitted column until cancel/submit resolves.
            setPmoStagedTaskIds(items.map((i) => String(i.id)));
            setPmoStagedTasks(items.map((i) => i.task).filter(Boolean));
            setPmoStagedCommitted(false);
            setPmoSubmissionItems(items);
            setPmoSubmissionDescriptionHtml('');
            setPmoSubmissionPendingFiles((prev) => {
                prev.forEach((p) => {
                    if (p?.preview) {
                        try {
                            URL.revokeObjectURL(p.preview);
                        } catch {
                            // ignore
                        }
                    }
                });
                return [];
            });
            setPmoSubmissionPendingLinks([]);
            setPmoSubmissionOpen(true);
            cancelQaTodoPmoSelectMode();
            cancelQaResolvedPmoSelectMode();
        },
        [selectedSprint, cancelQaTodoPmoSelectMode, cancelQaResolvedPmoSelectMode],
    );

    const openPmoSubmissionFromSubmission = useCallback(
        (submission) => {
            const list = Array.isArray(submission?.tasks) ? submission.tasks : [];
            if (list.length === 0) {
                message.warning('No tasks to submit');
                return;
            }
            openPmoSubmissionWithTodoTasks(list);
        },
        [openPmoSubmissionWithTodoTasks],
    );

    const openPmoSubmissionAllTodos = useCallback(() => {
        openPmoSubmissionWithTodoTasks(todos);
    }, [todos, openPmoSubmissionWithTodoTasks]);

    const openPmoSubmissionFromTodoSelection = useCallback(() => {
        const idSet = new Set(qaTodoPmoSelectedIds.map(String));
        const picked = todos.filter((t) => {
            const tid = getSprintTaskApiId(t) ?? t?.id;
            return tid != null && idSet.has(String(tid));
        });
        openPmoSubmissionWithTodoTasks(picked);
    }, [todos, qaTodoPmoSelectedIds, openPmoSubmissionWithTodoTasks]);

    const openPmoSubmissionAllResolved = useCallback(() => {
        openPmoSubmissionWithTodoTasks(resolvedTasks);
    }, [resolvedTasks, openPmoSubmissionWithTodoTasks]);

    const openPmoSubmissionFromResolvedSelection = useCallback(() => {
        const idSet = new Set(qaResolvedPmoSelectedIds.map(String));
        const picked = resolvedTasks.filter((t) => {
            const tid = getSprintTaskApiId(t) ?? t?.id;
            return tid != null && idSet.has(String(tid));
        });
        openPmoSubmissionWithTodoTasks(picked);
    }, [resolvedTasks, qaResolvedPmoSelectedIds, openPmoSubmissionWithTodoTasks]);

    useEffect(() => {
        cancelQaTodoPmoSelectMode();
        cancelQaResolvedPmoSelectMode();
    }, [selectedSprintId, cancelQaTodoPmoSelectMode, cancelQaResolvedPmoSelectMode]);

    useEffect(() => {
        // Changing sprint should never keep a staged overlay around.
        setPmoStagedTaskIds([]);
        setPmoStagedTasks([]);
        setPmoStagedCommitted(false);
    }, [selectedSprintId]);

    // Once the server reflects the PMO submission, drop the staged overlay.
    useEffect(() => {
        if (!pmoStagedCommitted) return;
        if (!pmoStagedTaskIds.length) {
            setPmoStagedCommitted(false);
            return;
        }
        const submittedIdSet = new Set(
            (Array.isArray(pmoRaw) ? pmoRaw : [])
                .map((t) => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId)
                .filter((x) => x != null)
                .map(String),
        );
        const allNowSubmitted = pmoStagedTaskIds.every((id) => submittedIdSet.has(String(id)));
        if (allNowSubmitted) {
            setPmoStagedTaskIds([]);
            setPmoStagedTasks([]);
            setPmoStagedCommitted(false);
        }
    }, [pmoStagedCommitted, pmoStagedTaskIds, pmoRaw]);

    const submitPmoFromModal = useCallback(
        (callbacks = {}) => {
            if (pmoSubmissionItems.length === 0) return;
            const milestoneId =
                pmoSubmissionItems[0]?.sprint?.milestoneId ?? pmoSubmissionItems[0]?.sprint?.milestone?.id;
            const sprintIds = [...new Set(pmoSubmissionItems.map((i) => String(i.sprintId)))];
            const taskIds = normalizePmoTaskIds(pmoSubmissionItems.map((i) => i.id));
            if (taskIds.length === 0) {
                message.error('No valid tasks to submit');
                return;
            }
            let notesMarkdown = '';
            const raw = pmoSubmissionDescriptionHtml.trim();
            if (raw) {
                try {
                    const forApi = contentToApiMentions(raw);
                    notesMarkdown = htmlToMarkdown(forApi).trim();
                } catch {
                    notesMarkdown = String(raw).trim();
                }
            }
            const attachmentUrls = pmoSubmissionPendingLinks.map(({ url, title }) => ({
                type: 'link',
                url,
                title: title || url,
            }));
            const files = pmoSubmissionPendingFiles.map((p) => p.file).filter(Boolean);
            submitToPMO({
                milestoneId,
                sprintIds,
                taskIds,
                notesMarkdown: notesMarkdown || undefined,
                attachmentUrls: attachmentUrls.length ? attachmentUrls : undefined,
                files: files.length ? files : undefined,
                onSuccess: () => {
                    setPmoStagedCommitted(true);
                    closePmoSubmissionPanel({ revertStaged: false });
                    callbacks.onSuccess?.();
                },
                onError: callbacks.onError,
            });
        },
        [
            pmoSubmissionItems,
            pmoSubmissionDescriptionHtml,
            pmoSubmissionPendingLinks,
            pmoSubmissionPendingFiles,
            submitToPMO,
            closePmoSubmissionPanel,
        ],
    );

    const [selectedTask, setSelectedTask] = useState(null);
    const [qaTaskDetailBugs, setQaTaskDetailBugs] = useState([]);
    const [showBugForm, setShowBugForm] = useState(false);
    const [editingPendingBugId, setEditingPendingBugId] = useState(null);
    const [pendingBugs, setPendingBugs] = useState([]);
    const [bugPendingAttachments, setBugPendingAttachments] = useState([]);
    const [bugPendingLinks, setBugPendingLinks] = useState([]);
    const [bugLinkModalOpen, setBugLinkModalOpen] = useState(false);
    const [bugLinkForm] = Form.useForm();
    const bugLinkCallbackRef = useRef(null);
    const [bugForm] = Form.useForm();
    const bugDescriptionHtml = Form.useWatch('description', bugForm);
    const selectedTaskDraftId = getQaBugDraftTaskId(selectedTask?.task);

    const taskDescriptionMarkdown = useMemo(() => {
        const task = selectedTask?.task;
        return task ? getTaskDescriptionMarkdown(task) : '';
    }, [selectedTask?.task]);

    const hydrateTaskDetail = useCallback(async (sprint, task, options = {}) => {
        if (!projectId || !sprint?.id || !task) return;
        const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
        if (!taskId) return;
        try {
            const detail = await queryClient.fetchQuery({
                queryKey: PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY(projectId, sprint.id, taskId),
                queryFn: () => getProjectSprintTaskDetail(projectId, sprint.id, taskId),
                staleTime: 60 * 1000,
            });
            const { task: fullTask, bugs: detailBugs } = extractTaskDetailPayload(detail, task);
            setSelectedTask((prev) => {
                if (!prev) return prev;
                if (String(prev?.sprint?.id ?? '') !== String(sprint.id)) return prev;
                const prevTaskId = getSprintTaskApiId(prev?.task) ?? prev?.task?.id ?? prev?.task?.taskId;
                if (String(prevTaskId ?? '') !== String(taskId)) return prev;
                return {
                    ...prev,
                    ...options,
                    sprint,
                    task: fullTask,
                };
            });
            setQaTaskDetailBugs(Array.isArray(detailBugs) ? detailBugs : []);
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || 'Failed to load task details');
        }
    }, [projectId, queryClient]);

    const openPanel = useCallback(
        (sprint, task, options = {}) => {
            const hideBugsSection = Boolean(options.hideBugsSection);
            /** View-only: attachments + bug list, no Create bug / submit (Bugs column, Submitted column). */
            const readOnlyBugActions = options.readOnlyBugActions === true;
            /** Open Bugs & Reports tab first (TODO column / drop-to-report flow). */
            const defaultBugsTab = options.defaultBugsTab === true;
            const tid = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
            const submissionForTask =
                Array.isArray(qaSubmissions) && tid != null
                    ? qaSubmissions.find((sub) => {
                        const list = Array.isArray(sub?.tasks) ? sub.tasks : [];
                        return list.some(
                            (t) =>
                                String(t?.taskId ?? t?.id ?? '') === String(task?.taskId ?? task?.id ?? '') ||
                                String(t?.sprintTaskId ?? t?.id ?? '') === String(tid),
                        );
                    }) ?? null
                    : null;

            setSelectedTask({
                sprint,
                task,
                submission: submissionForTask,
                hideBugsSection,
                readOnlyBugActions,
                defaultBugsTab,
            });
            setQaTaskDetailBugs(Array.isArray(task?.bugs) ? task.bugs : []);
            const draftTaskId = getQaBugDraftTaskId(task);
            const storedDrafts = loadQaBugDrafts(projectId, draftTaskId);
            const hydratedDrafts = storedDrafts.map((draft) => ({
                ...draft,
                status: 'draft',
                files: [],
                fileEntries: [],
            }));
            setPendingBugs(hydratedDrafts);
            setShowBugForm(false);
            setBugPendingAttachments([]);
            setBugPendingLinks([]);
            setBugLinkModalOpen(false);
            bugLinkCallbackRef.current = null;
            try {
                bugLinkForm.resetFields();
            } catch {
                /* ignore */
            }
            bugForm.resetFields();
            void hydrateTaskDetail(sprint, task, {
                hideBugsSection,
                readOnlyBugActions,
                defaultBugsTab,
            });
        },
        [bugForm, bugLinkForm, projectId, qaSubmissions, hydrateTaskDetail],
    );

    const closePanel = useCallback(() => {
        setSelectedTask(null);
        setQaTaskDetailBugs([]);
        setShowBugForm(false);
        setEditingPendingBugId(null);
        setBugPendingAttachments([]);
        setBugPendingLinks([]);
        setBugLinkModalOpen(false);
        bugLinkCallbackRef.current = null;
        try {
            bugLinkForm.resetFields();
        } catch {
            /* ignore */
        }
        bugForm.resetFields();
    }, [bugForm, bugLinkForm]);

    const openBugForm = useCallback(() => {
        setEditingPendingBugId(null);
        setShowBugForm(true);
    }, []);
    const closeBugForm = useCallback(() => {
        setShowBugForm(false);
        setEditingPendingBugId(null);
        setBugPendingAttachments([]);
        setBugPendingLinks([]);
        setBugLinkModalOpen(false);
        bugLinkCallbackRef.current = null;
        try {
            bugLinkForm.resetFields();
        } catch {
            /* ignore */
        }
        bugForm.resetFields();
    }, [bugForm, bugLinkForm]);

    const removePendingBug = useCallback((localId) => {
        setPendingBugs((prev) => {
            const target = prev.find((b) => b.localId === localId);
            const list = Array.isArray(target?.fileEntries) ? target.fileEntries : [];
            list.forEach((f) => {
                if (f?.preview) {
                    try {
                        URL.revokeObjectURL(f.preview);
                    } catch {
                        /* ignore */
                    }
                }
            });
            return prev.filter((b) => b.localId !== localId);
        });
    }, []);

    const startEditPendingBug = useCallback(
        (localId) => {
            const draft = pendingBugs.find((b) => b.localId === localId);
            if (!draft) return;
            setEditingPendingBugId(localId);
            bugForm.setFieldsValue({
                severity: draft.severity || 'high',
                description: draft.descriptionHtml || '',
            });
            const nextAttachments = (draft.fileEntries || []).map((entry, i) => ({
                id: entry.id || `qa-bug-edit-${Date.now()}-${i}`,
                file: entry.file,
                name: entry.name || entry.file?.name || 'attachment',
                type: entry.type || entry.file?.type || '',
                preview: entry.preview || null,
            }));
            setBugPendingAttachments(nextAttachments);
            setBugPendingLinks(Array.isArray(draft.attachments) ? draft.attachments.map((a, i) => ({
                id: a.id || `bug-link-edit-${Date.now()}-${i}`,
                url: a.url,
                title: a.title || a.url,
            })) : []);
            setShowBugForm(true);
        },
        [pendingBugs, bugForm],
    );

    const handleBugFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `qa-bug-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setBugPendingAttachments((prev) => [...prev, ...next]);
    }, []);

    const removeBugAttachment = useCallback(
        (id) => {
            setBugPendingAttachments((prev) => {
                const index = prev.findIndex((p) => p.id === id);
                if (index < 0) return prev.filter((p) => p.id !== id);
                let content = bugForm.getFieldValue('description') || '';
                const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                content = content.replace(linkRegex, '');
                for (let j = prev.length - 1; j > index; j--) {
                    content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                bugForm.setFieldValue('description', content);
                return prev.filter((p) => p.id !== id);
            });
        },
        [bugForm],
    );

    const removeBugAttachmentByEditorIndex = useCallback((index) => {
        if (index < 0) return;
        setBugPendingAttachments((prev) => {
            const target = prev[index];
            if (target?.preview) {
                try {
                    URL.revokeObjectURL(target.preview);
                } catch {
                    /* ignore */
                }
            }
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    const handleBugLinkRequest = useCallback(
        (insertLink) => {
            bugLinkCallbackRef.current = insertLink;
            bugLinkForm.resetFields();
            setBugLinkModalOpen(true);
        },
        [bugLinkForm],
    );

    const closeBugLinkModal = useCallback(() => {
        setBugLinkModalOpen(false);
        bugLinkCallbackRef.current = null;
    }, []);

    const handleBugLinkModalSubmit = useCallback(() => {
        bugLinkForm.validateFields().then((values) => {
            const rawUrl = (values.url || '').trim();
            const title = (values.title || '').trim();
            const check = validateLinkUrlInput(rawUrl);
            if (!check.ok) {
                bugLinkForm.setFields([
                    { name: 'url', errors: [check.message || 'Enter a valid URL (https://example.com)'] },
                ]);
                return;
            }
            const normalizedUrl = check.normalized;
            const displayTitle = title || normalizedUrl;
            const insertLink = bugLinkCallbackRef.current;
            if (insertLink) insertLink({ url: normalizedUrl, title: displayTitle });
            setBugPendingLinks((prev) => [
                ...prev,
                { id: `bug-link-${Date.now()}`, url: normalizedUrl, title: displayTitle },
            ]);
            setBugLinkModalOpen(false);
            bugLinkCallbackRef.current = null;
        });
    }, [bugLinkForm]);

    const removeBugPendingLink = useCallback(
        (idOrHref) => {
            const key = String(idOrHref ?? '').trim();
            if (!key) return;
            setBugPendingLinks((prev) => {
                const match =
                    prev.find((x) => String(x.id) === key) ??
                    prev.find((x) => String(x.url) === key);
                const next = prev.filter((x) => String(x.id) !== key && String(x.url) !== key);
                const href = match?.url || (key.startsWith('http') ? key : null);
                if (href) {
                    const esc = href.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const c = String(bugForm.getFieldValue('description') || '');
                    const re = new RegExp(`<a\\s(?=[^>]*href=["']${esc}["'])[^>]*>[\\s\\S]*?<\\/a>`, 'i');
                    bugForm.setFieldValue('description', c.replace(re, ''));
                }
                return next;
            });
        },
        [bugForm],
    );

    const handleBugEditorLinkRemoved = useCallback(
        (href) => {
            if (!href) return;
            removeBugPendingLink(String(href));
        },
        [removeBugPendingLink],
    );

    useEffect(() => {
        const html = bugDescriptionHtml;
        if (!html || typeof html !== 'string') {
            setBugPendingLinks([]);
            return;
        }
        setBugPendingLinks((prev) => prev.filter((x) => !x?.url || html.includes(String(x.url))));
    }, [bugDescriptionHtml]);

    const saveBugDraft = useCallback(
        (valuesArg) => {
            const values = valuesArg != null && typeof valuesArg === 'object' ? valuesArg : bugForm.getFieldsValue();
            const descriptionHtml = contentToApiMentions(values.description ?? '');
            const descriptionPayload = buildTaskDescription(descriptionHtml);
            if (!descriptionPayload?.markdown) {
                message.warning('Please add a description');
                return;
            }
            const previewText = String(descriptionPayload.markdown || '')
                .replace(/\s+/g, ' ')
                .trim()
                .slice(0, 160);
            const severity = values.severity || 'high';
            const assignedToIds = Array.isArray(values.assignedToIds) ? values.assignedToIds : [];
            const fileEntries = bugPendingAttachments.map((p, i) => ({
                id: p.id || `qa-bug-file-${Date.now()}-${i}`,
                file: p.file,
                name: p.name || p.file?.name || 'attachment',
                type: p.type || p.file?.type || '',
                preview: p.preview || null,
            }));
            const files = fileEntries.map((p) => p.file).filter(Boolean);
            const attachments = bugPendingLinks.map(({ id, url, title }) => ({ id, url, title: title || url }));
            setPendingBugs((prev) => {
                const payload = {
                    localId: editingPendingBugId || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    status: 'draft',
                    severity,
                    descriptionHtml: values.description ?? '',
                    descriptionMarkdown: descriptionPayload.markdown,
                    descriptionText: previewText,
                    assignedToIds,
                    ...(files.length > 0 && { files }),
                    ...(fileEntries.length > 0 && { fileEntries }),
                    ...(attachments.length > 0 && { attachments }),
                };
                if (!editingPendingBugId) return [...prev, payload];
                return prev.map((b) => (b.localId === editingPendingBugId ? payload : b));
            });
            setBugPendingAttachments([]);
            setBugPendingLinks([]);
            setEditingPendingBugId(null);
            closeBugForm();
        },
        [bugForm, closeBugForm, bugPendingAttachments, bugPendingLinks, editingPendingBugId],
    );

    useEffect(() => {
        if (!selectedTaskDraftId) return;
        const draftPayload = pendingBugs.map((b) => ({
            localId: b.localId,
            status: 'draft',
            severity: b.severity || 'high',
            descriptionHtml: b.descriptionHtml || '',
            descriptionMarkdown: b.descriptionMarkdown || '',
            descriptionText:
                (typeof b.descriptionText === 'string' && b.descriptionText.trim())
                || String(b.descriptionMarkdown || '')
                    .replace(/\s+/g, ' ')
                    .trim()
                    .slice(0, 160),
            assignedToIds: Array.isArray(b.assignedToIds) ? b.assignedToIds : [],
            attachments: Array.isArray(b.attachments) ? b.attachments : [],
        }));
        saveQaBugDrafts(projectId, selectedTaskDraftId, draftPayload);
    }, [projectId, selectedTaskDraftId, pendingBugs]);

    const submitAllBugs = useCallback(async () => {
        if (!selectedTask?.task) return;
        const taskId = getSprintTaskApiId(selectedTask.task) ?? selectedTask.task?.taskId ?? selectedTask.task?.id;
        if (taskId == null || taskId === '') {
            message.error('Task is missing task id');
            return;
        }
        const taskName = selectedTask.task.title ?? selectedTask.task.name ?? 'Task';
        if (pendingBugs.length === 0) {
            message.warning('Add at least one bug before submitting');
            return;
        }
        for (let i = 0; i < pendingBugs.length; i += 1) {
            const b = pendingBugs[i];
            const md = typeof b.descriptionMarkdown === 'string' ? b.descriptionMarkdown.trim() : '';
            if (!md) {
                message.warning(
                    `Bug draft ${i + 1} has no description text. Use Edit to add a description, or remove the draft.`,
                );
                return;
            }
        }
        try {
            const bugsPayload = pendingBugs.map((b) => ({
                description: { markdown: String(b.descriptionMarkdown ?? '').trim() },
                severity: b.severity || 'high',
                ...(Array.isArray(b.assignedToIds) &&
                    b.assignedToIds.length > 0 && { assignedToIds: b.assignedToIds }),
                ...(Array.isArray(b.files) && b.files.length > 0 && { files: b.files }),
                ...(Array.isArray(b.attachments) && b.attachments.length > 0 && { attachments: b.attachments }),
            }));
            await createBugAsync({ taskId, bugs: bugsPayload });
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: PROJECT_BUGS_QUERY_KEY(projectId), refetchType: 'all' }),
                queryClient.invalidateQueries({ queryKey: PROJECT_QA_SUBMISSIONS_QUERY_KEY(projectId), refetchType: 'all' }),
            ]);
            setPendingBugs([]);
            saveQaBugDrafts(projectId, taskId, []);
            if (selectedTask?.sprint?.id) {
                await queryClient.invalidateQueries({
                    queryKey: PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY(projectId, selectedTask.sprint.id, taskId),
                });
                await hydrateTaskDetail(selectedTask.sprint, selectedTask.task, {
                    hideBugsSection: Boolean(selectedTask?.hideBugsSection),
                    readOnlyBugActions: Boolean(selectedTask?.readOnlyBugActions),
                    defaultBugsTab: true,
                });
            }
            setShowBugForm(false);
            setEditingPendingBugId(null);
            setBugPendingAttachments([]);
            setBugPendingLinks([]);
            setBugLinkModalOpen(false);
            bugForm.resetFields();
            message.success(`Submitted ${pendingBugs.length} bug(s) for ${taskName}`);
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || 'Failed to submit bugs');
        }
    }, [selectedTask, pendingBugs, createBugAsync, projectId, queryClient, hydrateTaskDetail, bugForm]);

    const handleTaskSelect = useCallback(
        (sprint, task, options = {}) => {
            openPanel(sprint, task, options);
        },
        [openPanel],
    );

    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const openSubmissionDetail = useCallback(
        (sprint, submissionGroup, options = {}) => {
            if (!sprint?.id || !submissionGroup) return;
            setSelectedSubmission({
                sprint,
                group: submissionGroup,
                rawSubmission: submissionGroup?.rawSubmission ?? null,
                defaultTab: options?.defaultTab ?? null,
            });
        },
        [],
    );
    const closeSubmissionDetail = useCallback(() => setSelectedSubmission(null), []);

    const refreshSelectedTaskDetail = useCallback(async () => {
        if (!selectedTask?.sprint?.id || !selectedTask?.task) return;
        await hydrateTaskDetail(selectedTask.sprint, selectedTask.task, {
            hideBugsSection: Boolean(selectedTask?.hideBugsSection),
            readOnlyBugActions: Boolean(selectedTask?.readOnlyBugActions),
            defaultBugsTab: Boolean(selectedTask?.defaultBugsTab),
        });
    }, [hydrateTaskDetail, selectedTask]);

    /** Submitted-to-PMO column: view task + attachments + bugs; no Create bug / submit. */
    const handlePmoSubmittedTaskOpenDetail = useCallback(
        (sprint, task) => {
            openPanel(sprint, task, { readOnlyBugActions: true });
        },
        [openPanel],
    );

    const handleDropReportBugs = useCallback(
        ({ sprintId, taskId }) => {
            const sprint = sprintList.find((s) => String(s.id) === String(sprintId));
            const task = Array.isArray(sprint?.tasks)
                ? sprint.tasks.find(
                    (t) => String(getSprintTaskApiId(t) ?? t?.id ?? '') === String(taskId),
                )
                : null;
            if (sprint && task) {
                openPanel(sprint, task, { defaultBugsTab: true });
            } else {
                message.error('Task not found');
            }
        },
        [sprintList, openPanel],
    );

    const [dropTarget, setDropTarget] = useState(null);
    const [draggingColumn, setDraggingColumn] = useState(null);
    const [draggingSelectedCount, setDraggingSelectedCount] = useState(0);

    const clearDropTarget = useCallback(() => {
        setDropTarget(null);
        setDraggingColumn(null);
        setDraggingSelectedCount(0);
    }, []);

    const resolveMutation = useMutation({
        mutationFn: ({ sprintId, taskId }) => {
            return resolveProjectSprintTask(projectId, sprintId, taskId);
        },
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
            queryClient.invalidateQueries({ queryKey: PROJECT_QA_SUBMISSIONS_QUERY_KEY(projectId) });
        },
    });

    const resolveTask = useCallback(
        (sprintId, taskId, callbacks = {}) => {
            resolveMutation.mutate(
                { sprintId, taskId },
                {
                    onSuccess: () => {
                        message.success('Task marked as Resolved');
                        callbacks.onSuccess?.();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || err?.message || 'Failed to resolve task');
                        callbacks.onError?.(err);
                    },
                }
            );
        },
        [resolveMutation, projectId]
    );

    const revertMutation = useMutation({
        mutationFn: ({ sprintId, taskId }) => {
            return revertProjectSprintTaskToQA(projectId, sprintId, taskId);
        },
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
            queryClient.invalidateQueries({ queryKey: PROJECT_QA_SUBMISSIONS_QUERY_KEY(projectId) });
        },
    });

    const revertTaskToQA = useCallback(
        (sprintId, taskId, callbacks = {}) => {
            revertMutation.mutate(
                { sprintId, taskId },
                {
                    onSuccess: () => {
                        message.success('Task reverted to Submitted to QA');
                        callbacks.onSuccess?.();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || err?.message || 'Failed to revert task');
                        callbacks.onError?.(err);
                    },
                }
            );
        },
        [revertMutation, projectId]
    );

    const handleTaskMove = useCallback(
        ({ sprintId, taskId, sourceColumn, targetColumn, selectedTaskIds, submissionId }) => {
            clearDropTarget();
            if (sprintId == null || sprintId === '' || !sourceColumn || !targetColumn) {
                return;
            }
            if (sourceColumn === targetColumn) return;

            if (targetColumn === 'resolved') {
                const ids = Array.isArray(selectedTaskIds) ? selectedTaskIds.map(String).filter(Boolean) : [];
                if (ids.length > 1) {
                    message.warning('Multiple tasks cannot be resolved at once via drag and drop.');
                    return;
                }
                if (!taskId) return;
                resolveTask(sprintId, taskId);
                return;
            }

            if (sourceColumn === 'resolved' && targetColumn === 'todo') {
                const ids = Array.isArray(selectedTaskIds) ? selectedTaskIds.map(String).filter(Boolean) : [];
                if (ids.length > 1) {
                    cancelQaResolvedPmoSelectMode();
                    void (async () => {
                        try {
                            for (const tid of ids) {
                                await revertMutation.mutateAsync({ sprintId, taskId: tid });
                            }
                            message.success(`Reverted ${ids.length} task(s) to Submitted to QA`);
                        } catch (err) {
                            message.error(err?.response?.data?.message || err?.message || 'Failed to revert tasks');
                        }
                    })();
                    return;
                }
                if (!taskId) return;
                revertTaskToQA(sprintId, taskId);
                return;
            }

            if (sourceColumn === 'todo' && targetColumn === 'bugs') {
                const ids = Array.isArray(selectedTaskIds) ? selectedTaskIds.map(String).filter(Boolean) : [];
                if (ids.length > 1) {
                    message.warning('Multiple tasks can only be dropped to Submitted (PMO).');
                    return;
                }
                if (!taskId) return;
                handleDropReportBugs({ sprintId, taskId });
                return;
            }
            if ((sourceColumn === 'todo' || sourceColumn === 'resolved') && targetColumn === 'pmo') {
                if (submissionId) {
                    const submission =
                        Array.isArray(qaSubmissions) && submissionId != null
                            ? qaSubmissions.find((s) => String(extractSubmissionId(s) ?? '') === String(submissionId))
                            : null;
                    if (!submission) {
                        message.error('Submission not found');
                        return;
                    }
                    const sprintTasks = (Array.isArray(submission?.tasks) ? submission.tasks : []).filter(
                        (t) => String(t?.sprintId ?? t?.sprint?.id ?? '') === String(sprintId),
                    );
                    openPmoSubmissionWithTodoTasks(sprintTasks);
                    return;
                }
                const ids = Array.isArray(selectedTaskIds) ? selectedTaskIds.map(String).filter(Boolean) : [];
                if (ids.length > 1) {
                    const idSet = new Set(ids);
                    const sourceTasks = sourceColumn === 'resolved' ? resolvedTasks : todos;
                    const picked = sourceTasks.filter((t) => {
                        const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                        return tid != null && idSet.has(String(tid));
                    });
                    openPmoSubmissionWithTodoTasks(picked);
                } else {
                    if (!taskId) return;
                    addToPmoSubmissionFromDrop({ sprintId, taskId });
                }
                return;
            }
            message.warning('This move is not supported');
        },
        [
            clearDropTarget,
            handleDropReportBugs,
            addToPmoSubmissionFromDrop,
            todos,
            resolvedTasks,
            openPmoSubmissionWithTodoTasks,
            qaSubmissions,
            resolveTask,
            revertTaskToQA,
            cancelQaResolvedPmoSelectMode,
            revertMutation
        ],
    );

    const handleDndDragStart = useCallback((payload) => {
        const sourceColumn = payload?.sourceColumn ?? null;
        setDraggingColumn(sourceColumn);
        if (payload?.type === 'submission') {
            const ids = Array.isArray(payload?.selectedTaskIds) ? payload.selectedTaskIds.map(String).filter(Boolean) : [];
            setDraggingSelectedCount(Math.max(ids.length, 1));
            return;
        }
        const ids = Array.isArray(payload?.selectedTaskIds) ? payload.selectedTaskIds.map(String).filter(Boolean) : [];
        setDraggingSelectedCount(ids.length);
    }, []);

    const handleDndDragOverColumn = useCallback((columnId, payload) => {
        const ids = Array.isArray(payload?.selectedTaskIds) ? payload.selectedTaskIds.map(String).filter(Boolean) : [];
        const isMulti = ids.length > 1;
        if (isMulti && columnId !== 'pmo') {
            setDropTarget(null);
            return;
        }
        setDropTarget(columnId);
    }, []);

    const handleDndDragEnd = useCallback(
        ({ overColumnId, payload }) => {
            const targetColumn = overColumnId ?? null;
            const sprintId = payload?.sprintId;
            let taskId = payload?.taskId;
            const submissionId = payload?.type === 'submission' ? payload?.submissionId : null;
            const sourceColumn = payload?.sourceColumn;
            const selectedTaskIds = payload?.selectedTaskIds;
            if (
                payload?.type === 'submission' &&
                (taskId == null || taskId === '') &&
                Array.isArray(selectedTaskIds) &&
                selectedTaskIds.length === 1
            ) {
                taskId = selectedTaskIds[0];
            }
            handleTaskMove({ sprintId, taskId, sourceColumn, targetColumn, selectedTaskIds, submissionId });
        },
        [handleTaskMove],
    );

    const todoSubmissionGroups = useMemo(() => {
        const sprint = selectedSprint;
        if (!sprint?.id) return [];
        const sprintId = sprint.id;

        const sprintTaskById = new Map();
        for (const st of Array.isArray(sprint.tasks) ? sprint.tasks : []) {
            const tid = getSprintTaskApiId(st) ?? st?.id ?? st?.taskId;
            if (tid == null || String(tid).trim() === '' || String(tid) === 'undefined') continue;
            sprintTaskById.set(String(tid), st);
        }

        const todoIdSet = new Set(
            [
                ...(Array.isArray(todos) ? todos : []),
                ...(Array.isArray(bugsReported) ? bugsReported : []).filter(
                    (t) => String(t.status ?? '').toLowerCase() !== 'submitted_to_pm'
                )
            ]
                .map((t) => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId)
                .filter((x) => x != null && String(x).trim() !== '' && String(x) !== 'undefined')
                .map(String),
        );
        if (todoIdSet.size === 0) return [];

        const list = Array.isArray(qaSubmissions) ? qaSubmissions : [];
        const groups = [];

        for (let idx = 0; idx < list.length; idx++) {
            const sub = list[idx];
            const sid = extractSubmissionId(sub) ?? `sub-${idx}`;
            const n = getSubmissionNumber(sub, idx);
            const labelN = n != null ? n : idx + 1;
            const sidLabel = String(labelN).toUpperCase().startsWith('SUB-') ? labelN : `SUB-${labelN}`;
            const submissionTitle = `Submission ${labelN}`;
            const statusLabel = sub?.status ?? sub?.qaStatus ?? sub?.submissionStatus ?? '';

            const tasksRaw = (Array.isArray(sub?.tasks) ? sub.tasks : []).map((t) => {
                if (t && typeof t === 'object') return t;
                const found = sprintTaskById.get(String(t));
                if (found) return found;
                return { id: t, taskId: t, sprintId };
            });
            // In noSprintsMode the sprint is a virtual 'no-sprint' bucket that
            // contains tasks from all real DB sprints. Skip the sprintId filter
            // so submission tasks are not excluded by their actual sprintId.
            const sprintTasks = sprintId === 'no-sprint'
                ? tasksRaw
                : tasksRaw.filter((t) => String(t?.sprintId ?? t?.sprint?.id ?? '') === String(sprintId));
            const todoTasksForSub = sprintTasks
                .filter((t) => {
                    const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                    return tid != null && todoIdSet.has(String(tid));
                })
                .map((t) => {
                    const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                    const canon = tid != null ? sprintTaskById.get(String(tid)) : null;
                    return canon ? { ...canon, ...t } : t;
                });
            if (todoTasksForSub.length === 0) continue;

            const attachmentCount =
                sub?.attachmentCount ??
                sub?.attachmentsCount ??
                normalizeSubmissionAttachmentRecords(sub).length;

            const notesMarkdown =
                (sub?.notes && typeof sub.notes === 'object' ? sub.notes.markdown : null) ??
                (typeof sub?.notes === 'string' ? sub.notes : '') ??
                '';
            const submissionMentions =
                sub?.qaSubmission?.descriptionMentions ??
                sub?.qaSubmission?.notesMentions ??
                sub?.descriptionMentions ??
                sub?.notesMentions ??
                null;
            const resolvedNotes = resolveDescriptionMentionsForDisplay(notesMarkdown, submissionMentions);

            groups.push({
                id: sid,
                sidLabel,
                submissionTitle,
                statusLabel,
                descriptionMarkdown: resolvedNotes,
                attachmentCount,
                submittedAtLabel: getSubmissionSubmittedAt(sub),
                submittedBy: getSubmissionSubmittedBy(sub),
                tasks: todoTasksForSub,
                rawSubmission: sub,
                onSubmit: () => openPmoSubmissionWithTodoTasks(todoTasksForSub),
            });
        }

        return groups;
    }, [selectedSprint, todos, bugsReported, qaSubmissions, openPmoSubmissionWithTodoTasks]);

    const pmoSubmissionGroups = useMemo(() => {
        const sprint = selectedSprint;
        if (!sprint?.id) return [];
        const sprintId = sprint.id;

        const sprintTaskById = new Map();
        for (const st of Array.isArray(sprint.tasks) ? sprint.tasks : []) {
            const tid = getSprintTaskApiId(st) ?? st?.id ?? st?.taskId;
            if (tid == null || String(tid).trim() === '' || String(tid) === 'undefined') continue;
            sprintTaskById.set(String(tid), st);
        }

        const pmoIdSet = new Set(
            [
                ...(Array.isArray(submittedToPmo) ? submittedToPmo : []),
                ...(Array.isArray(bugsReported) ? bugsReported : []).filter(
                    (t) => String(t.status ?? '').toLowerCase() === 'submitted_to_pm'
                )
            ]
                .map((t) => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId)
                .filter((x) => x != null && String(x).trim() !== '' && String(x) !== 'undefined')
                .map(String),
        );
        if (pmoIdSet.size === 0) return [];

        const list = Array.isArray(pmoSubmissions) ? pmoSubmissions : [];
        const groups = [];

        for (let idx = 0; idx < list.length; idx++) {
            const sub = list[idx];
            const sid = extractSubmissionId(sub) ?? `pmo-sub-${idx}`;
            const n = getSubmissionNumber(sub, idx);
            const labelN = n != null ? n : idx + 1;
            const sidLabel = String(labelN).toUpperCase().startsWith('PMO-') ? labelN : `PMO-${labelN}`;
            const submissionTitle = `Submission ${labelN}`;
            const statusLabel = sub?.status ?? sub?.qaStatus ?? sub?.submissionStatus ?? '';

            const tasksRaw = (Array.isArray(sub?.tasks) ? sub.tasks : []).map((t) => {
                if (t && typeof t === 'object') return t;
                const found = sprintTaskById.get(String(t));
                if (found) return found;
                return { id: t, taskId: t, sprintId };
            });
            const sprintTasks = sprintId === 'no-sprint'
                ? tasksRaw
                : tasksRaw.filter((t) => String(t?.sprintId ?? t?.sprint?.id ?? '') === String(sprintId));
            const pmoTasksForSub = sprintTasks
                .filter((t) => {
                    const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                    return tid != null && pmoIdSet.has(String(tid));
                })
                .map((t) => {
                    const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                    const canon = tid != null ? sprintTaskById.get(String(tid)) : null;
                    return canon ? { ...canon, ...t } : t;
                });
            if (pmoTasksForSub.length === 0) continue;

            const attachmentCount =
                sub?.attachmentCount ??
                sub?.attachmentsCount ??
                normalizeSubmissionAttachmentRecords(sub).length;

            const notesMarkdown =
                (sub?.notes && typeof sub.notes === 'object' ? sub.notes.markdown : null) ??
                (typeof sub?.notes === 'string' ? sub.notes : '') ??
                '';
            const submissionMentions =
                sub?.qaSubmission?.descriptionMentions ??
                sub?.qaSubmission?.notesMentions ??
                sub?.descriptionMentions ??
                sub?.notesMentions ??
                null;
            const resolvedNotes = resolveDescriptionMentionsForDisplay(notesMarkdown, submissionMentions);

            groups.push({
                id: sid,
                sidLabel,
                submissionTitle,
                statusLabel,
                descriptionMarkdown: resolvedNotes,
                attachmentCount,
                submittedAtLabel: getSubmissionSubmittedAt(sub),
                submittedBy: getSubmissionSubmittedBy(sub),
                tasks: pmoTasksForSub,
                rawSubmission: sub,
            });
        }

        return groups;
    }, [selectedSprint, submittedToPmo, bugsReported, pmoSubmissions]);

    const isTabLoading = Boolean(
        bugsLoading ||
        (useMilestonesEnabled && milestonesLoading && milestoneList.length === 0) ||
        (usePhasesEnabled && phasesLoading && phases.length === 0)
    );

    const isBoardFetching = Boolean(
        useSprintsEnabled && selectedSprintId
            ? (submissionsLoading || isSprintQueryLoading)
            : false
    );

    return {
        milestones: activeTabList,
        sprints: sprintList,
        selectedMilestoneId,
        setSelectedMilestoneId,
        sprintsForMilestone,
        selectedSprintId,
        setSelectedSprintId,
        selectedSprint,
        todos,
        resolvedTasks,
        qaSubmissions,
        todoSubmissionGroups,
        pmoSubmissionGroups,
        bugsReported,
        submittedToPmo,
        isLoading: isTabLoading,
        isBoardFetching,
        submitToPMO,
        submitToPMOPending,
        pmoSubmissionOpen,
        closePmoSubmissionPanel,
        pmoSubmissionItems,
        pmoSubmissionDescriptionHtml,
        setPmoSubmissionDescriptionHtml,
        pmoSubmissionPendingFiles,
        pmoSubmissionPendingLinks,
        handlePmoFilesSelected,
        removePmoPendingFile,
        removePmoPendingLink,
        handlePmoLinkRequest,
        handlePmoEditorLinkRemoved,
        pmoLinkModalOpen,
        pmoLinkForm,
        closePmoLinkModal,
        handlePmoLinkModalSubmit,
        pmoSubmissionSession,
        removeFromPmoSubmission,
        submitPmoFromModal,
        selectedTask,
        taskDescriptionMarkdown,
        refreshSelectedTaskDetail,
        qaTaskDetailBugs,
        openPanel,
        closePanel,
        showBugForm,
        openBugForm,
        closeBugForm,
        editingPendingBugId,
        pendingBugs,
        removePendingBug,
        startEditPendingBug,
        bugForm,
        saveBugDraft,
        submitAllBugs,
        createBugPending,
        bugPendingAttachments,
        bugPendingLinks,
        handleBugFilesSelected,
        removeBugAttachment,
        removeBugAttachmentByEditorIndex,
        bugLinkModalOpen,
        bugLinkForm,
        handleBugLinkRequest,
        closeBugLinkModal,
        handleBugLinkModalSubmit,
        removeBugPendingLink,
        handleBugEditorLinkRemoved,
        handleTaskSelect,
        selectedSubmission,
        openSubmissionDetail,
        closeSubmissionDetail,
        handlePmoSubmittedTaskOpenDetail,
        dropTarget,
        draggingColumn,
        draggingSelectedCount,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        clearDropTarget,
        qaTodoPmoSelectMode,
        qaTodoPmoSelectedIds,
        toggleQaTodoPmoTaskSelected,
        startQaTodoPmoSelectMode,
        cancelQaTodoPmoSelectMode,
        selectAllQaTodoTasks,
        unselectAllQaTodoTasks,
        qaResolvedPmoSelectMode,
        qaResolvedPmoSelectedIds,
        toggleQaResolvedPmoTaskSelected,
        startQaResolvedPmoSelectMode,
        cancelQaResolvedPmoSelectMode,
        selectAllQaResolvedTasks,
        unselectAllQaResolvedTasks,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        openPmoSubmissionAllTodos,
        openPmoSubmissionFromTodoSelection,
        openPmoSubmissionAllResolved,
        openPmoSubmissionFromResolvedSelection,
        useSprintsEnabled,
        noSprintsMode,
        useMilestonesEnabled,
        usePhasesEnabled,
        isTodoPmoTaskDisabled,
        isResolvedPmoTaskDisabled,
    };
}
